// src/components/policies/PolicyEditor.js
import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';

const PolicyEditor = ({
  content = '',
  onChange,
  placeholder = 'Start writing...',
  editable = true,
  minHeight = '300px'
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      if (onChange) {
        onChange(editor.getHTML());
      }
    },
  });

  if (!editor) {
    return null;
  }

  // Text-based toolbar button — guaranteed to render
  const ToolbarButton = ({ onClick, isActive, label, title }) => (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault(); // Prevent editor blur
        onClick();
      }}
      style={{
        padding: '4px 8px',
        borderRadius: '4px',
        backgroundColor: isActive ? '#7c3aed' : 'transparent',
        color: isActive ? '#FFFFFF' : '#4B5563',
        border: 'none',
        cursor: 'pointer',
        fontSize: '13px',
        fontWeight: isActive ? '600' : '500',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        lineHeight: '1.4',
        transition: 'all 0.15s ease',
        minWidth: '28px',
        textAlign: 'center',
      }}
      title={title}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = '#F3F4F6';
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      {label}
    </button>
  );

  const ToolbarDivider = () => (
    <div style={{ width: '1px', height: '20px', backgroundColor: '#D1D5DB', margin: '0 4px' }} />
  );

  return (
    <div style={{ border: '1px solid #E5E7EB', borderRadius: '8px', overflow: 'hidden' }}>
      {/* Toolbar */}
      {editable && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            padding: '6px 8px',
            backgroundColor: '#F9FAFB',
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          {/* Text Formatting */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            label="B"
            title="Bold"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            label={<span style={{ fontStyle: 'italic' }}>I</span>}
            title="Italic"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            label={<span style={{ textDecoration: 'underline' }}>U</span>}
            title="Underline"
          />

          <ToolbarDivider />

          {/* Headings */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive('heading', { level: 2 })}
            label={<span style={{ fontSize: '13px' }}>H2</span>}
            title="Heading 2"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            isActive={editor.isActive('heading', { level: 3 })}
            label={<span style={{ fontSize: '12px' }}>H3</span>}
            title="Heading 3"
          />

          <ToolbarDivider />

          {/* Lists */}
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            label="•"
            title="Bullet List"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            label="1."
            title="Numbered List"
          />

          <ToolbarDivider />

          {/* Text Alignment */}
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            isActive={editor.isActive({ textAlign: 'left' })}
            label="≡"
            title="Align Left"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            isActive={editor.isActive({ textAlign: 'center' })}
            label="≡"
            title="Align Center"
          />

          <ToolbarDivider />

          {/* Undo/Redo */}
          <ToolbarButton
            onClick={() => editor.chain().focus().undo().run()}
            isActive={false}
            label="↩"
            title="Undo"
          />
          <ToolbarButton
            onClick={() => editor.chain().focus().redo().run()}
            isActive={false}
            label="↪"
            title="Redo"
          />
        </div>
      )}

      {/* Editor Content */}
      <div
        style={{
          backgroundColor: '#FFFFFF',
          padding: '16px',
          minHeight,
          fontSize: '14px',
          lineHeight: '1.6',
        }}
      >
        <EditorContent editor={editor} />
      </div>

      {/* TipTap Editor Styles */}
      <style>{`
        .ProseMirror {
          outline: none;
          min-height: ${minHeight};
        }

        .ProseMirror:focus {
          outline: none;
        }

        .ProseMirror h2 {
          font-size: 20px;
          font-weight: 600;
          margin: 24px 0 12px;
          color: #1F2937;
        }

        .ProseMirror h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 20px 0 8px;
          color: #1F2937;
        }

        .ProseMirror p {
          margin: 8px 0;
          line-height: 1.6;
          color: #374151;
        }

        .ProseMirror ul,
        .ProseMirror ol {
          padding-left: 24px;
          margin: 8px 0;
        }

        .ProseMirror li {
          margin: 4px 0;
          color: #374151;
        }

        .ProseMirror li p {
          margin: 2px 0;
        }

        .ProseMirror blockquote {
          border-left: 3px solid #E5E7EB;
          padding-left: 16px;
          color: #6B7280;
          margin: 12px 0;
        }

        .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9CA3AF;
          pointer-events: none;
          height: 0;
        }

        .ProseMirror [data-text-align="center"] {
          text-align: center;
        }

        .ProseMirror [data-text-align="left"] {
          text-align: left;
        }
      `}</style>
    </div>
  );
};

export default PolicyEditor;
